import { useMemo, useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Loader from "../ui/Loader";
import { summarizePlanPayments } from "../../services/exportTratamenteService";
import { usePatients } from "../../context/PatientsContext";
import { useTratamente } from "../../context/TratamenteContext";
import { getScopeLabel, normalizeScope } from "../../utils/treatmentScope";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalizeIdValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isPlaceholderName = (value) => {
  if (typeof value !== "string") return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.includes("@")) return true;
  if (/^[0-9a-f]{24}$/i.test(trimmed)) return true;
  return false;
};

const resolveMedicDisplayName = (medic, fallback = "Medic necunoscut") => {
  if (!medic || typeof medic !== "object") return fallback;
  const sources = [
    medic.name,
    medic.fullName,
    medic.displayName,
    medic.label,
    medic.lastName && medic.firstName
      ? `${medic.lastName} ${medic.firstName}`
      : "",
  ];
  for (const source of sources) {
    if (typeof source !== "string") continue;
    const trimmed = source.trim();
    if (!trimmed || isPlaceholderName(trimmed)) continue;
    return trimmed;
  }
  return fallback;
};

const normalizeRecords = (records) =>
  Array.isArray(records)
    ? records.filter((record) => record && typeof record === "object")
    : [];

// 🔹 Construim o cheie unică pentru deduplicare
const buildRecordKey = (record) => {
  if (!record || typeof record !== "object") return "";

  const servicesFragment = (record.services || [])
    .map((srv) => {
      const tooth = srv?.tooth ?? srv?.toothNumber ?? srv?.toothName ?? "";
      const name = srv?.name || "";
      const price = Number(srv?.finalPrice ?? srv?.price ?? 0).toFixed(2);
      return `${tooth}|${name}|${price}`;
    })
    .sort()
    .join(";");

  const totalFragment = Number(record.total || 0).toFixed(2);
  const methodFragment = (record.method || "").toLowerCase();
  const descriptionFragment = (record.description || "").toLowerCase();
  const createdFragment = record.createdAt
    ? new Date(record.createdAt).toISOString()
    : `${totalFragment}|${methodFragment}|${descriptionFragment}|${servicesFragment}|${Math.random()}`;

  return `${totalFragment}|${methodFragment}|${descriptionFragment}|${createdFragment}|${servicesFragment}`;
};

// 🔹 Eliminăm duplicatele
const deduplicate = (records) => {
  const unique = [];
  const seenKeys = new Set();

  records.forEach((record) => {
    const key = buildRecordKey(record); // 👈 eliminăm pdfData din cheie
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    unique.push(record);
  });

  return unique;
};

export default function FinancialHistoryModal({
  patientId,
  records = [],
  onClose,
  patientName,
  treatments = [],
  commissionMeta = null,
  commissionLoading = false,
}) {
  const [activeTab, setActiveTab] = useState("proforme");
  const { deleteFinancialRecord } = usePatients();
  const { fetchForPatient } = useTratamente();

  const sortedRecords = normalizeRecords(records).sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );

  // 🔹 Aplica deduplicare
  const proforme = deduplicate(sortedRecords.filter((r) => r.pdfData));
  const payments = deduplicate(sortedRecords.filter((r) => !r.pdfData));

  const effectiveTab = activeTab;

  const commissionSummary = useMemo(() => {
    const normalizeEmailValue = (value) =>
      typeof value === "string" ? value.trim().toLowerCase() : "";

    const medics = Array.isArray(commissionMeta?.medics)
      ? commissionMeta.medics
          .filter((medic) => medic && typeof medic === "object")
          .map((medic) => {
            const email = medic.email || "";
            const id = typeof medic.id === "string" ? medic.id : "";
            const normalizedEmail = normalizeEmailValue(
              medic.normalizedEmail || email
            );
            const normalizedId = normalizeIdValue(
              medic.normalizedId || id
            );
            const name = resolveMedicDisplayName(
              medic,
              email || id || "Medic necunoscut"
            );
            return {
              id,
              email,
              name,
              normalizedEmail,
              normalizedId,
            };
          })
      : [];

    const referrerEmail = commissionMeta?.referrer?.email || "";
    const referrerNormalized = normalizeEmailValue(
      commissionMeta?.referrer?.normalizedEmail || referrerEmail
    );
    const referrerId = commissionMeta?.referrer?.id || "";
    const referrerNormalizedId = normalizeIdValue(
      commissionMeta?.referrer?.normalizedId || referrerId
    );
    if ((referrerEmail || referrerId) && !medics.length) {
      medics.push({
        id: referrerId,
        email: referrerEmail,
        name: resolveMedicDisplayName(
          commissionMeta?.referrer,
          referrerEmail || "Medic necunoscut"
        ),
        normalizedEmail: referrerNormalized,
        normalizedId: referrerNormalizedId,
      });
    }

    const treatmentsList = Array.isArray(treatments) ? treatments : [];
    if (!treatmentsList.length) {
      return {
        primaryRows: [],
        retreatRows: [],
        totals: {
          primaryRevenue: 0,
          primaryCommission: 0,
          retreatRevenue: 0,
          retreatCommission: 0,
        },
      };
    }

    const summary = summarizePlanPayments(treatmentsList);
    const entryMap = new Map(summary.entries.map((entry) => [entry.id, entry]));
    const resolveOwner =
      typeof commissionMeta?.resolveOwner === "function"
        ? commissionMeta.resolveOwner
        : null;

    const ensureMedic = (ownerRaw) => {
      const owner =
        typeof ownerRaw === "string" ? ownerRaw : String(ownerRaw || "");
      const normalizedEmail = normalizeEmailValue(owner);
      const normalizedId = normalizeIdValue(owner);

      if (!normalizedEmail && !normalizedId) {
        const fallback = medics[0] || {
          id: "",
          email: "",
          name: "Medic necunoscut",
          normalizedEmail: "necunoscut",
          normalizedId: "necunoscut",
        };
        if (!medics.length) medics.push(fallback);
        return fallback;
      }

      let medic = null;

      if (normalizedEmail && owner.includes("@")) {
        medic =
          medics.find((item) => item.normalizedEmail === normalizedEmail) ||
          null;
      }
      if (!medic && normalizedId) {
        medic =
          medics.find((item) => item.normalizedId === normalizedId) || null;
      }

      if (!medic) {
        medic = {
          id: normalizedId ? owner : "",
          email: owner.includes("@") ? owner : "",
          name: resolveMedicDisplayName(
            { name: owner },
            owner.includes("@") ? owner : "Medic necunoscut"
          ),
          normalizedEmail,
          normalizedId,
        };
        medics.push(medic);
      }
      return medic;
    };

    const parseDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const monthKeyFromDate = (date) => {
      if (!date) return null;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
    };

    const now = new Date();
    const currentMonthKey = monthKeyFromDate(now);

    const monthsAggregator = new Map();

    const ensureMonthBucket = (monthKey) => {
      if (!monthsAggregator.has(monthKey)) {
        monthsAggregator.set(monthKey, new Map());
      }
      return monthsAggregator.get(monthKey);
    };

    const ensureMedicBucket = (monthMap, medic) => {
      const medicKey =
        medic.normalizedId ||
        normalizeIdValue(medic.id) ||
        medic.normalizedEmail ||
        normalizeEmailValue(medic.email) ||
        "necunoscut";
      if (!monthMap.has(medicKey)) {
        monthMap.set(medicKey, {
          id: medic.id,
          email: medic.email,
          name: medic.name,
          normalizedEmail: medic.normalizedEmail,
          normalizedId: medic.normalizedId,
          primary: { count: 0, revenue: 0, commission: 0 },
          retreat: { count: 0, revenue: 0, commission: 0 },
        });
      }
      return monthMap.get(medicKey);
    };

    const getCommissionTag = (service) =>
      service?.commissionTag || service?.meta?.commissionTag || "";
    const isPrimaryService = (service) => {
      const tag = getCommissionTag(service);
      if (tag === "primary") return true;
      return (service?.name || "").toLowerCase().includes("tratament primar");
    };
    const isRetreatService = (service) => {
      const tag = getCommissionTag(service);
      if (tag === "retreat") return true;
      return (service?.name || "").toLowerCase().includes("retratament");
    };

    const resolveCategoryDate = (treatment, entry, category) => {
      const services = Array.isArray(treatment.services)
        ? treatment.services
        : [];
      const match = services.find((srv) =>
        category === "retreat" ? isRetreatService(srv) : isPrimaryService(srv)
      );
      const date =
        parseDate(match?.paidAt) ||
        parseDate(match?.doneAt) ||
        parseDate(entry?.paymentSummary?.paidAt) ||
        parseDate(treatment.updatedAt) ||
        parseDate(treatment.createdAt) ||
        now;
      return monthKeyFromDate(date) || currentMonthKey;
    };

    const sumServiceRevenue = (list = []) =>
      list.reduce(
        (sum, srv) =>
          sum + Number(srv?.finalPrice ?? srv?.price ?? srv?.basePrice ?? 0),
        0
      );

    treatmentsList.forEach((treatment) => {
      const services = Array.isArray(treatment.services)
        ? treatment.services
        : [];
      if (!services.length) return;

      const entry = entryMap.get(treatment.id);
      if (!entry) return;

      const hasRetreat = services.some((srv) => isRetreatService(srv));
      const hasPrimary = services.some((srv) => isPrimaryService(srv));

      if (!hasRetreat && !hasPrimary) return;

      const retreatServices = services.filter((srv) => isRetreatService(srv));
      const primaryServices = services.filter((srv) => isPrimaryService(srv));
      const retreatRevenue = sumServiceRevenue(retreatServices);
      const primaryRevenue = sumServiceRevenue(primaryServices);
      if (!(retreatRevenue > 0) && !(primaryRevenue > 0)) return;

      let ownerEmailRaw = "";
      if (resolveOwner) {
        try {
          ownerEmailRaw = resolveOwner(treatment, entry) || "";
        } catch {
          ownerEmailRaw = commissionMeta?.referrer?.email || "";
        }
      } else if (commissionMeta?.referrer?.email) {
        ownerEmailRaw = commissionMeta.referrer.email;
      } else if (medics.length) {
        ownerEmailRaw = medics[0].email;
      }

      const ownerMedic = ensureMedic(ownerEmailRaw);
      const monthKey = resolveCategoryDate(
        treatment,
        entry,
        hasRetreat ? "retreat" : "primary"
      );
      const monthMap = ensureMonthBucket(monthKey);
      const bucket = ensureMedicBucket(monthMap, ownerMedic);

      if (hasRetreat) {
        bucket.retreat.count += 1;
        bucket.retreat.revenue += retreatRevenue;
        bucket.retreat.commission += retreatRevenue * 0.1;
      }
      if (hasPrimary) {
        bucket.primary.count += 1;
        bucket.primary.revenue += primaryRevenue;
        bucket.primary.commission += primaryRevenue * 0.2;
      }
    });

    const buildRowsFromMonthMap = (monthMap) => {
      const primaryRows = [];
      const retreatRows = [];
      const totals = {
        primaryRevenue: 0,
        primaryCommission: 0,
        retreatRevenue: 0,
        retreatCommission: 0,
      };

      monthMap.forEach((value) => {
        if (value.primary.count) {
          primaryRows.push({
            id: value.id,
            email: value.email,
            name: value.name,
            count: value.primary.count,
            revenue: value.primary.revenue,
            commission: value.primary.commission,
          });
          totals.primaryRevenue += value.primary.revenue;
          totals.primaryCommission += value.primary.commission;
        }
        if (value.retreat.count) {
          retreatRows.push({
            id: value.id,
            email: value.email,
            name: value.name,
            count: value.retreat.count,
            revenue: value.retreat.revenue,
            commission: value.retreat.commission,
          });
          totals.retreatRevenue += value.retreat.revenue;
          totals.retreatCommission += value.retreat.commission;
        }
      });

      primaryRows.sort((a, b) => b.revenue - a.revenue);
      retreatRows.sort((a, b) => b.revenue - a.revenue);

      return { primaryRows, retreatRows, totals };
    };

    const archives = [];

    monthsAggregator.forEach((monthMap, monthKey) => {
      const summary = buildRowsFromMonthMap(monthMap);
      if (monthKey === currentMonthKey) {
        monthsAggregator.set(monthKey, summary);
      } else {
        const [year, month] = monthKey.split("-");
        const labelDate = new Date(Number(year), Number(month) - 1, 1);
        const label = labelDate.toLocaleDateString("ro-RO", {
          year: "numeric",
          month: "long",
        });
        archives.push({
          monthKey,
          label,
          ...summary,
        });
      }
    });

    archives.sort((a, b) => (a.monthKey < b.monthKey ? 1 : -1));

    const current =
      monthsAggregator.get(currentMonthKey) ||
      buildRowsFromMonthMap(new Map());

    return {
      current,
      archives,
    };
  }, [treatments, commissionMeta]);

  const handleDownload = (record) => {
    if (!record?.pdfData) return;
    const link = document.createElement("a");
    link.href = record.pdfData;
    link.download = record.fileName || `plan-${record.id || "fin"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteRecord = async (record) => {
    if (!patientId || !record?.id) return;
    if (record.type !== "payment") return;
    const confirmed = window.confirm(
      "Ștergi această plată? Serviciile aferente vor reveni ca neplătite, iar avansul se ajustează automat."
    );
    if (!confirmed) return;
    try {
      await deleteFinancialRecord(patientId, record.id);
      await fetchForPatient(patientId);
    } catch (err) {
      console.error("Eroare ștergere plată:", err);
      alert(err.message || "Nu s-a putut șterge plata.");
    }
  };

  const renderRecords = (list, type) => {
    if (list.length === 0) {
      return (
        <p className="text-sm text-gray-600">
          {type === "proforme"
            ? "Nu există facturi proforme salvate pentru acest pacient."
            : "Nu există plăți înregistrate pentru acest pacient."}
        </p>
      );
    }

    const totalSum = list.reduce(
      (sum, record) => sum + Number(record.total || 0),
      0
    );

    return (
      <>
        {list.map((record) => (
          <div
            key={record.id}
            className="border rounded-lg p-3 shadow-sm bg-white flex flex-col gap-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-700 space-y-1">
                <p>
                  <strong>Data:</strong>{" "}
                  {record.createdAt
                    ? new Date(record.createdAt).toLocaleString("ro-RO")
                    : "-"}
                </p>
                <p>
                  <strong>Total:</strong> {formatCurrency(record.total)} RON
                </p>
                {record.method && (
                  <p>
                    <strong>Metodă:</strong> {record.method}
                  </p>
                )}
                {(record?.extra?.tooth || record?.extra?.scope) && (
                  <p>
                    <strong>Zonă:</strong>{" "}
                    {getScopeLabel(
                      normalizeScope(record.extra?.scope, record.extra?.tooth),
                      record.extra?.tooth
                    )}
                  </p>
                )}
                {record.description && (
                  <p>
                    <strong>Descriere:</strong> {record.description}
                  </p>
                )}
                <p>
                  <strong>Servicii:</strong> {record.services?.length || 0}
                </p>
                {record?.extra?.advanceDelta
                  ? (
                      <p>
                        <strong>Avans ajustat:</strong>{" "}
                        {formatCurrency(Math.abs(record.extra.advanceDelta))} RON{" "}
                        {record.extra.advanceDelta > 0 ? "(depozitat)" : "(consumat)"}
                      </p>
                    )
                  : null}
              </div>

              {record.pdfData && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownload(record)}
                >
                  ⬇️ Descarcă
                </Button>
              )}
              {type === "payments" && record.type === "payment" && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteRecord(record)}
                >
                  🗑️ Șterge plata
                </Button>
              )}
            </div>

            {record.services?.length ? (
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1">
                {record.services.map((srv, idx) => (
                  <li key={`${record.id}-srv-${idx}`}>
                    {getScopeLabel(normalizeScope(srv.scope, srv.tooth), srv.tooth)}:{" "}
                    {srv.name} – {formatCurrency(srv.finalPrice)} RON
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
        <div className="border-t pt-3 mt-2 text-right text-sm font-semibold text-gray-800">
          Total înregistrări: {formatCurrency(totalSum)} RON
        </div>
      </>
    );
  };

  const renderCommissionSection = (title, rows, totals) => {
    if (!rows.length) {
      return (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
          <p className="text-xs text-gray-500">
            Nu există comisioane calculate pentru această categorie.
          </p>
        </div>
      );
    }

    const totalCount = rows.reduce((sum, row) => sum + row.count, 0);
    const totalRevenue =
      totals?.revenue ??
      rows.reduce((sum, row) => sum + row.revenue, 0);
    const totalCommission =
      totals?.commission ??
      rows.reduce((sum, row) => sum + row.commission, 0);

    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">Medic</th>
                <th className="px-3 py-2 text-right">Dinți</th>
                <th className="px-3 py-2 text-right">Încasări</th>
                <th className="px-3 py-2 text-right">Comision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${title}-${row.id || row.email || row.name}`}
                  className="border-t"
                >
                  <td className="px-3 py-2">
                    {row.name || row.email || "Medic necunoscut"}
                  </td>
                  <td className="px-3 py-2 text-right">{row.count}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrency(row.revenue)} RON
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-blue-700">
                    {formatCurrency(row.commission)} RON
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr className="border-t">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{totalCount}</td>
                <td className="px-3 py-2 text-right">
                  {formatCurrency(totalRevenue)} RON
                </td>
                <td className="px-3 py-2 text-right">
                  {formatCurrency(totalCommission)} RON
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderCommissionTab = () => {
    if (commissionLoading) {
      return <Loader text="Se calculează comisioanele..." />;
    }

    const current = commissionSummary.current;

    if (
      current.primaryRows.length === 0 &&
      current.retreatRows.length === 0
    ) {
      return (
        <p className="text-sm text-gray-600">
          Nu există comisioane calculate pentru acest pacient.
        </p>
      );
    }

    const totalCount =
      current.primaryRows.reduce((sum, row) => sum + row.count, 0) +
      current.retreatRows.reduce((sum, row) => sum + row.count, 0);
    const totalRevenue =
      current.totals.primaryRevenue + current.totals.retreatRevenue;
    const totalCommission =
      current.totals.primaryCommission + current.totals.retreatCommission;

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Total general comisioane
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-blue-900">
            <div className="flex flex-col">
              <span className="uppercase text-[11px] tracking-wide text-blue-700/80">
                Dinți procesați
              </span>
              <span className="text-lg font-semibold">{totalCount}</span>
            </div>
            <div className="flex flex-col">
              <span className="uppercase text-[11px] tracking-wide text-blue-700/80">
                Încasări totale
              </span>
              <span className="text-lg font-semibold">
                {formatCurrency(totalRevenue)} RON
              </span>
            </div>
            <div className="flex flex-col">
              <span className="uppercase text-[11px] tracking-wide text-blue-700/80">
                Comision total
              </span>
              <span className="text-lg font-semibold">
                {formatCurrency(totalCommission)} RON
              </span>
            </div>
          </div>
        </div>

        {renderCommissionSection(
          "Comisioane din tratamente primare (20%)",
          current.primaryRows,
          {
            revenue: current.totals.primaryRevenue,
            commission: current.totals.primaryCommission,
          }
        )}
        {renderCommissionSection(
          "Comisioane din retratamente (10%)",
          current.retreatRows,
          {
            revenue: current.totals.retreatRevenue,
            commission: current.totals.retreatCommission,
          }
        )}
      </div>
    );
  };

  const renderArchiveTab = () => {
    const archives = commissionSummary.archives || [];
    if (commissionLoading) {
      return <Loader text="Se calculează comisioanele arhivate..." />;
    }
    if (!archives.length) {
      return (
        <p className="text-sm text-gray-600">
          Nu există arhive de comisioane pentru acest pacient.
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {archives.map((archive) => {
          const totalCount =
            archive.primaryRows.reduce((sum, row) => sum + row.count, 0) +
            archive.retreatRows.reduce((sum, row) => sum + row.count, 0);
          const totalRevenue =
            archive.totals.primaryRevenue + archive.totals.retreatRevenue;
          const totalCommission =
            archive.totals.primaryCommission + archive.totals.retreatCommission;

          return (
            <div
              key={`archive-${archive.monthKey}`}
              className="space-y-4 rounded-2xl border border-gray-200 bg-white/70 p-4 shadow-sm"
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-sm sm:text-base font-semibold text-gray-800">
                  {archive.label}
                </h4>
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="mr-4">
                    <strong>Dinți:</strong> {totalCount}
                  </span>
                  <span className="mr-4">
                    <strong>Încasări:</strong> {formatCurrency(totalRevenue)} RON
                  </span>
                  <span>
                    <strong>Comision:</strong>{" "}
                    {formatCurrency(totalCommission)} RON
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {renderCommissionSection(
                  "Comisioane din tratamente primare (20%)",
                  archive.primaryRows,
                  {
                    revenue: archive.totals.primaryRevenue,
                    commission: archive.totals.primaryCommission,
                  }
                )}
                {renderCommissionSection(
                  "Comisioane din retratamente (10%)",
                  archive.retreatRows,
                  {
                    revenue: archive.totals.retreatRevenue,
                    commission: archive.totals.retreatCommission,
                  }
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      title={`Financiar – ${patientName || "Pacient"}`}
      onClose={onClose}
      size="lg"
    >
      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            effectiveTab === "proforme"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("proforme")}
        >
          Facturi Proforme
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            effectiveTab === "payments"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("payments")}
        >
          Plăți înregistrate
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            effectiveTab === "commissions"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("commissions")}
        >
          Comisioane medici
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            effectiveTab === "archive"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("archive")}
        >
          Arhivă
        </button>
      </div>

      <div className="p-4 space-y-3 h-[80vh] overflow-y-auto">
        {effectiveTab === "proforme"
          ? renderRecords(proforme, "proforme")
          : effectiveTab === "payments"
            ? renderRecords(payments, "payments")
            : effectiveTab === "commissions"
              ? renderCommissionTab()
              : renderArchiveTab()}
      </div>
    </Modal>
  );
}
